/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { IAgendaUpdate } from '../../../../../app/resource/agenda/agenda.types';
import { AgendaItem } from './AgendaItem';
import { IAgendaItemsFormProps } from './types';

export const AgendaItemsForm: FunctionComponent<IAgendaItemsFormProps> = ({
  agendaElement,
  setAgendaElement,
  setEditableItem,
  onAdd,
  setOnAdd,
  onFormModify,
}) => {
  const { control, setError, errors, setValue } = useFormContext();
  // eslint-disable-next-line prefer-const
  const { fields, append, remove, insert, move } = useFieldArray<IAgendaUpdate>({ control, name: 'agendaItems' });
  const [disabled, setDisabled] = useState(true);
  const handleChange = useCallback(() => {
    setDisabled(!disabled);
    onFormModify();
  }, [disabled, onFormModify]);

  const removeAgendaItem = useCallback(
    (index) => {
      onFormModify();
      if (fields.length > 0) {
        remove(index);
      } else {
        setError('agendaItems', { type: 'minLength', message: 'You need to add at least one entry' });
      }
    },
    [fields.length, remove, setError, onFormModify],
  );

  useEffect(() => {
    if (agendaElement.index !== undefined) {
      fields.forEach((field, index) => {
        if (index === agendaElement.index) {
          Object.keys(field).forEach((key) => {
            setValue(`agendaItems.${index}.${key}`, agendaElement[key]);
          });
        }
      });

      setAgendaElement({
        id: '',
        title: '',
        embedUrl: '',
        checked: true,
        duration: '5',
        description: '',
        sort: '',
        index: undefined,
        keyName: '',
      });
    } else {
      if (agendaElement.title !== '') {
        append({
          title: agendaElement.title,
          id: undefined,
          checked: agendaElement.checked ?? true,
          duration: agendaElement.duration,
          description: agendaElement.description,
          sort: agendaElement.sort,
          keyName: agendaElement.keyName,
          boardId: agendaElement.boardId,
          boardListId: agendaElement.boardListId,
          embedUrl: agendaElement.embedUrl,
          tempKey: new Date().getTime(),
        });

        setAgendaElement({
          id: '',
          title: '',
          embedUrl: '',
          checked: true,
          duration: '5',
          description: '',
          sort: '',
          keyName: '',
        });
      }
      if (onAdd && fields.length > 1 && fields[fields.length - 2].keyName === 'conclusion') {
        move(fields.length - 2, fields.length - 1);
        setOnAdd(false);
      }
    }
  }, [agendaElement, append, setAgendaElement, insert, fields, move, setValue, removeAgendaItem, onAdd, setOnAdd]);

  const { watch } = useFormContext();

  const times = useMemo(() => {
    return fields.map((field, index) =>
      disabled
        ? +watch(`agendaItems.${index}.checked`) === 1
          ? +watch(`agendaItems.${index}.duration`)
          : 0
        : +watch(`agendaItems.${index}.checked`) === 1
        ? +watch(`agendaItems.${index}.duration`)
        : 0,
    );
  }, [disabled, fields, watch]);

  const showTime = useMemo(() => times.reduce((prev, curr) => (curr ? curr : 0) + prev, 0), [times]);

  const handleDrop = useCallback(
    (result: DropResult) => {
      onFormModify();
      setOnAdd(false);
      const sourceIndex = result.source.index;
      if (result.destination) {
        const destinationIndex = result.destination?.index;
        move(sourceIndex, destinationIndex);
      }
    },
    [move, onFormModify, setOnAdd],
  );

  return (
    <>
      <div className="row">
        <div className="col-12 mb-2" style={{ display: 'none' }}>
          <div className="remember-checkbox flex aic jcl">
            <input
              type="checkbox"
              id="defaultCheckbox"
              name="defaultCheckbox"
              onChange={handleChange}
              checked={false}
            />
            <label htmlFor="defaultCheckbox">Default 90 Minute Meeting</label>
          </div>
        </div>

        <div className="col-12">
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId={`agendaItems`}>
              {(provided) => (
                <div
                  className="row meeting90-container"
                  style={{ flexDirection: 'column' }}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {fields.map(
                    (field, index) =>
                      field && (
                        <Draggable key={String(field.tempKey)} draggableId={String(field.tempKey)} index={index}>
                          {(provided) => (
                            <div
                              className="col-6 meeting-list-item"
                              key={index}
                              style={{ maxWidth: 'none', width: '100%' }}
                            >
                              <AgendaItem
                                removeAgendaItem={removeAgendaItem}
                                index={index}
                                agendaItem={field}
                                defaultCheckbox={disabled}
                                provided={provided}
                                setEditableItem={setEditableItem}
                                onFormModify={onFormModify}
                              />
                            </div>
                          )}
                        </Draggable>
                      ),
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="row flex aic py-2">
            <div className="col-12 flex aic jcr flex-row-reverse">
              <p>
                Total Meeting Duration: <span className="total_minutes">{showTime}</span> minute
              </p>
            </div>
          </div>
        </div>
      </div>
      {!!errors.agendaItems?.message && (
        <div>
          <p>{errors.agendaItems.message}</p>
        </div>
      )}
    </>
  );
};
